import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { DatePicker, Select, SelectItem } from '@nextui-org/react';
import { getLocalTimeZone, parseDate, today } from '@internationalized/date';
import { I18nProvider } from '@react-aria/i18n';
import { CtyButton } from '../button/CtyButton';
import { formatDateLocale, getUserLocaleAndTimezone } from '../utils/dateutils';

interface DateFilterComponentProps {
  parentFilterInstance?: (callback: (instance: any) => void) => void;
  api?: any;
  column?: { getColId?: () => string };
  onFilterChange?: (value: string, field: string) => void;
  disabled?: boolean;
  /** When set, the header shows this model instead of waiting for AG Grid's filter instance. */
  getDisplayModel?: () => any;
  subscribeDisplayModel?: (listener: () => void) => () => void;
}

function floatingDateLabel(model: any): { selectedDate: string; rangeLabel: string } {
  if (!model?.dateFrom) return { selectedDate: '', rangeLabel: '' };
  const from = String(model.dateFrom).split(' ')[0];
  if (model.type === 'inRange' && model.dateTo) {
    const to = String(model.dateTo).split(' ')[0];
    return { selectedDate: from, rangeLabel: `${formatDateLocale(from)} to ${formatDateLocale(to)}` };
  }
  return { selectedDate: from, rangeLabel: '' };
}

// Shared date filter component for AG Grid floating filters.
// Works as a proper AG Grid floatingFilterComponent (integrates with parent CustomDateFilter).
const DateFilterComponent = forwardRef(
  ({
    parentFilterInstance,
    api,
    column,
    onFilterChange,
    disabled = false,
    getDisplayModel,
    subscribeDisplayModel,
  }: DateFilterComponentProps, ref) => {
    const [selectedDate, setSelectedDate] = useState(() => floatingDateLabel(getDisplayModel?.()).selectedDate);
    const [rangeLabel, setRangeLabel] = useState(() => floatingDateLabel(getDisplayModel?.()).rangeLabel);

    const syncFromModel = useCallback((parentModel: any) => {
      const next = floatingDateLabel(parentModel);
      setSelectedDate(next.selectedDate);
      setRangeLabel(next.rangeLabel);
    }, []);

    useImperativeHandle(ref, () => ({
      onParentModelChanged(parentModel: any) {
        // An empty parent while the filter instance is still mounting is not a user clear.
        if (!parentModel?.dateFrom && getDisplayModel?.()?.dateFrom) return;
        syncFromModel(parentModel);
      },
    }), [syncFromModel, getDisplayModel]);

    useEffect(() => {
      if (getDisplayModel && subscribeDisplayModel) {
        const apply = () => syncFromModel(getDisplayModel() ?? null);
        apply();
        return subscribeDisplayModel(apply);
      }

      const colId = column?.getColId?.();
      if (!api?.addEventListener || !colId || typeof api.getFilterModel !== 'function') return;
      const syncFromGrid = () => syncFromModel(api.getFilterModel()?.[colId] ?? null);
      api.addEventListener('filterChanged', syncFromGrid);
      return () => api.removeEventListener?.('filterChanged', syncFromGrid);
    }, [api, column, syncFromModel, getDisplayModel, subscribeDisplayModel]);

    const dateChange = (date: string) => {
      setSelectedDate(date);
      setRangeLabel('');

      if (parentFilterInstance) {
        parentFilterInstance((instance: any) => {
          if (date) {
            instance.setModel({
              type: 'equals',
              dateFrom: `${date} 00:00:00`,
              filterType: 'date',
            });
          } else {
            instance.setModel(null);
          }
          api?.onFilterChanged?.();
        });
      }

      if (onFilterChange) {
        onFilterChange(date, 'date');
      }
    };

    const ClearDate = (
      <div className="flex justify-between p-2">
        <CtyButton variant="bordered" isDisabled={!selectedDate} onPress={() => dateChange('')}>
          Clear
        </CtyButton>
        <CtyButton color="primary" onPress={() => dateChange(today(getLocalTimeZone()).toString())}>
          Today
        </CtyButton>
      </div>
    );
    const maxValue = today(getLocalTimeZone());
    const { locale } = getUserLocaleAndTimezone();

    if (rangeLabel) {
      return (
        <div
          className="flex items-center h-full w-full min-h-[32px] px-2 text-xs text-foreground whitespace-nowrap"
          title={rangeLabel}
        >
          {rangeLabel}
        </div>
      );
    }

    return (
      <I18nProvider locale={locale}>
        <DatePicker
          value={selectedDate ? parseDate(selectedDate) : null}
          CalendarBottomContent={ClearDate}
          onChange={(date) => dateChange(date?.toString() || '')}
          maxValue={maxValue}
          isDisabled={disabled}
        />
      </I18nProvider>
    );
  },
);

const DATE_FILTER_OPTIONS = [
  { key: 'equals', label: 'Equals' },
  { key: 'notEqual', label: 'Does not equal' },
  { key: 'lessThan', label: 'Before' },
  { key: 'greaterThan', label: 'After' },
  { key: 'inRange', label: 'Between' },
] as const;

const CustomDateFilter = forwardRef((_props: any, ref) => {
  const [filterType, setFilterType] = useState<string>('equals');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  // AG Grid reads getModel() in the same turn as setModel(). React state is still
  // the previous value then, so the floating filter would stay blank.
  const modelRef = useRef({ type: 'equals', dateFrom: '', dateTo: '' });

  const { locale } = getUserLocaleAndTimezone();
  const maxValue = today(getLocalTimeZone());

  const writeModel = (next: { type: string; dateFrom: string; dateTo: string }) => {
    modelRef.current = next;
    setFilterType(next.type);
    setDateFrom(next.dateFrom);
    setDateTo(next.dateTo);
  };

  const isActive = useCallback(() => {
    const current = modelRef.current;
    if (current.type === 'inRange') return Boolean(current.dateFrom && current.dateTo);
    return Boolean(current.dateFrom);
  }, []);

  const buildModel = useCallback(() => {
    if (!isActive()) return null;
    const current = modelRef.current;
    return {
      filterType: 'date',
      type: current.type,
      dateFrom: current.dateFrom ? `${current.dateFrom} 00:00:00` : null,
      dateTo: current.type === 'inRange' && current.dateTo ? `${current.dateTo} 00:00:00` : null,
    };
  }, [isActive]);

  useImperativeHandle(ref, () => ({
    isFilterActive: isActive,
    getModel: buildModel,
    setModel(model: any) {
      if (!model) {
        writeModel({ type: 'equals', dateFrom: '', dateTo: '' });
        setTimeout(() => _props.filterChangedCallback?.(), 0);
        return;
      }
      writeModel({
        type: model.type || 'equals',
        dateFrom: model.dateFrom ? String(model.dateFrom).split(' ')[0] : '',
        dateTo: model.dateTo ? String(model.dateTo).split(' ')[0] : '',
      });
    },
    doesFilterPass(params: any) {
      const cellValue = _props.valueGetter
        ? _props.valueGetter(params)
        : params.data?.[_props.colDef?.field];
      if (!cellValue) return false;
      const cellDate = new Date(cellValue);
      if (Number.isNaN(cellDate.getTime())) return false;
      const cellDay = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate()).getTime();

      if (!dateFrom) return true;
      const fromDay = new Date(`${dateFrom}T00:00:00`).getTime();

      switch (filterType) {
        case 'equals':
          return cellDay === fromDay;
        case 'notEqual':
          return cellDay !== fromDay;
        case 'lessThan':
          return cellDay < fromDay;
        case 'greaterThan':
          return cellDay > fromDay;
        case 'inRange': {
          if (!dateTo) return true;
          const toDay = new Date(`${dateTo}T00:00:00`).getTime();
          return cellDay >= fromDay && cellDay <= toDay;
        }
        default:
          return true;
      }
    },
  }));

  const onFilterChanged = () => {
    _props.filterChangedCallback?.();
  };

  const handleTypeChange = (keys: any) => {
    const val = String([...keys][0] ?? 'equals');
    writeModel({
      type: val,
      dateFrom: modelRef.current.dateFrom,
      dateTo: val === 'inRange' ? modelRef.current.dateTo : '',
    });
    setTimeout(onFilterChanged, 0);
  };

  const handleDateFromChange = (date: any) => {
    writeModel({ ...modelRef.current, dateFrom: date?.toString() || '' });
    setTimeout(onFilterChanged, 0);
  };

  const handleDateToChange = (date: any) => {
    writeModel({ ...modelRef.current, dateTo: date?.toString() || '' });
    setTimeout(onFilterChanged, 0);
  };

  const handleReset = () => {
    writeModel({ type: 'equals', dateFrom: '', dateTo: '' });
    setTimeout(onFilterChanged, 0);
  };

  return (
    <I18nProvider locale={locale}>
      <div className="flex flex-col gap-2 p-3 min-w-[220px]">
        <Select
          label=""
          size="sm"
          disallowEmptySelection
          selectedKeys={new Set([filterType])}
          onSelectionChange={handleTypeChange}
          aria-label="Date filter condition"
        >
          {DATE_FILTER_OPTIONS.map((opt) => (
            <SelectItem key={opt.key} value={opt.key}>
              {opt.label}
            </SelectItem>
          ))}
        </Select>
        <DatePicker
          label=""
          value={dateFrom ? parseDate(dateFrom) : null}
          onChange={handleDateFromChange}
          maxValue={maxValue}
          size="sm"
        />
        {filterType === 'inRange' && (
          <DatePicker
            label=""
            value={dateTo ? parseDate(dateTo) : null}
            onChange={handleDateToChange}
            maxValue={maxValue}
            size="sm"
          />
        )}
        <div className="flex justify-end">
          <CtyButton size="sm" variant="bordered" onPress={handleReset} isDisabled={!dateFrom && !dateTo}>
            Reset
          </CtyButton>
        </div>
      </div>
    </I18nProvider>
  );
});

DateFilterComponent.displayName = 'DateFilterComponent';
CustomDateFilter.displayName = 'CustomDateFilter';

export { DateFilterComponent, CustomDateFilter };
